/* eslint-disable unicorn/filename-case */
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
  useEffect,
} from "react";
import { app, dialog, HostClientType } from "@microsoft/teams-js";
import config from "../config/config";
import * as microsoftTeams from "@microsoft/teams-js";
import { useLocalStorage } from "./useStorage";
import { QueryClient } from "react-query";
import { logger } from "../Logger";
import { Constants } from "../common/constants";
import { RequestUtils } from "../services/RequestUtils";
import { ApiResponse } from "../types/ApiRequest";
import { AuthenticationUtils } from "../components/Authentication/AuthenticationUtils";
import {
  ISAMConfig,
  AuthCallResponse,
  IAuthCallResponse,
  TeamsContextType,
} from "../types/CommonTypes";
import { LocalizationService } from "../services/TcTeamsAppLocaleService";
import { Configuration } from "../services/configuration";
import { Providers, ProviderState, TeamsHelper } from "@microsoft/mgt-element";
import { TeamsFxProvider } from "@microsoft/mgt-teamsfx-provider";
import { DialogSize } from "@microsoft/teams-js";

/**
 * Returns the full context of our teams application, with current theme, user information, and teamsUserCredential utilities
 *
 */
export const useTCTeamsContext = (
  teamsContext: TeamsContextType,
  queryClient: QueryClient
): {
  isLoginFromBot: boolean;
  configuration: any;
  userConfiguration: any;
  teamcenterLanguage: any;
  i18n: any;
  teamcenter: {
    session: IAuthCallResponse;
    setSession: (value: IAuthCallResponse) => void;
    isAuthenticated: boolean;
    setTCisAuthenticated: (value: boolean) => void;
    isAuthenticating: boolean;
    logout: () => void;
    login: () => Promise<void>;
    setLogin: (teamcenterCode: IAuthCallResponse) => Promise<void>;
    siteDisplay: string;
  };
  failure: {
    error:
      | {
          title: string;
          message: string;
          statusCode: number;
          correlationId: string;
        }
      | undefined;
    setError: Dispatch<
      SetStateAction<
        | {
            title: string;
            message: string;
            statusCode: number;
            correlationId: string;
          }
        | undefined
      >
    >;
  };
  queryClient: QueryClient;
  scopesArray: Array<string>;
  isLoginGraph: boolean;
} => {
  logger.logTrace("Entered useTcTeamsContext()");

  const [isLoginFromBot, setIsLoginFromBot] = useState<boolean>(false);
  const [conversationId, setConversationId] = useState<string | undefined>(
    undefined
  );
  const [tokenSid, setTokenSid] = useState<string | undefined>(undefined);
  const [taskDetailOpened, setTaskDetailOpened] = useState<boolean>(false);
  const [context, setContext] = useState<app.Context | undefined>(undefined);
  const [i18n, setI18n] = useState<{ [key: string]: string }>({});

  const [teamcenterCode, setTeamcenterCode] = useLocalStorage<
    AuthCallResponse | undefined
  >(Constants.cacheTeamcenterCode, undefined);
  const [error, setError] = useState<
    | {
        title: string;
        message: string;
        statusCode: number;
        correlationId: string;
      }
    | undefined
  >(undefined);
  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    teamcenterCode !== undefined && !!teamcenterCode.sessionId
  );

  const [configuration, setConfiguration] = useLocalStorage<any>(
    Constants.cacheConfiguration,
    undefined
  );
  const [userConfiguration, setUserConfiguration] = useLocalStorage<any>(
    Constants.cacheUserConfiguration,
    undefined
  );
  const [teamcenterLanguage, setTeamcenterLanguage] = useLocalStorage<
    string | undefined
  >(Constants.cacheTeamcenterLanguage, undefined);

  const [isLoginGraph, setIsLoginGraph] = useLocalStorage<any>(
    "isLoginGraph",
    false
  );

  const [siteDisplay, setSiteDisplay] = useState<string>("");

  const scopesArray = useMemo(() => {
    return ["User.Read", "User.ReadBasic.All"];
  }, []);

  const [consentNeeded, setConsentNeeded] = useState<boolean>(false);

  // Memoize the TeamsFxProvider initialization
  const provider = useMemo(() => {
    if (!teamsContext.teamsUserCredential) return;
    const providerInstance = new TeamsFxProvider(
      teamsContext.teamsUserCredential,
      scopesArray
    );
    Providers.globalProvider = providerInstance;
    TeamsHelper.microsoftTeamsLib = microsoftTeams;
    return providerInstance;
  }, [teamsContext.teamsUserCredential, scopesArray]);

  // Check the token and set the provider state using useEffect
  useEffect(() => {
    const init = async () => {
      try {
        if (!teamsContext.teamsUserCredential) return;
        let token =
          await teamsContext.teamsUserCredential.getToken(scopesArray);
        setIsLoginGraph(true);
        Providers.globalProvider.setState(ProviderState.SignedIn);
      } catch (error) {
        setConsentNeeded(true);
        setIsLoginGraph(false);
      }
    };

    init();
  }, [provider]);

  // Handle user consent
  useEffect(() => {
    (async () => {
      try {
        if (!consentNeeded) return;
        if (!teamsContext.teamsUserCredential) return;
        await teamsContext.teamsUserCredential.login(scopesArray);
        Providers.globalProvider.setState(ProviderState.SignedIn);
        setConsentNeeded(false);
        setIsLoginGraph(true);
      } catch (error) {
        console.error("Error during consent:", error);
        setIsLoginGraph(false);
      }
    })();
  }, [consentNeeded]);

  // handler to check if the user is logging in from bot
  // and set the conversationId if available
  const handleIsLoginFromBot = useCallback((taskDetails: string) => {
    const [
      capabilityName,
      capabilityId,
      task_type,
      task_value,
      taskId,
      taskIdValue,
      tokenid,
      tokenSidValue,
    ] = taskDetails.split("|");
    if (task_type === "isLoginFromBot") {
      const isLoginFromBotValue = task_value === "true";
      setIsLoginFromBot(isLoginFromBotValue);
      if (taskId === "conversationId") {
        setConversationId(taskIdValue);
      }
      if (tokenid === "sid") {
        setTokenSid(tokenSidValue);
      }
      logger.logInformation(
        `isLoginFromBot set to ${isLoginFromBotValue} with ${taskId}`
      );
    }
  }, []);

  interface TaskDetailsData {
    partId: string;
    eventType: string;
    eventUser: string;
    eventTime: string;
  }

  function isTaskDetailsData(obj: any): obj is TaskDetailsData {
    return (
      typeof obj === "object" &&
      obj !== null &&
      typeof obj.partId === "string" &&
      typeof obj.eventType === "string" &&
      typeof obj.eventUser === "string" &&
      typeof obj.eventTime === "string"
    );
  }

  const handleEventNotification = useCallback((taskDetails: string) => {
    logger.logInformation(
      `Handling event notification with details: ${taskDetails}`
    );
    try {
      const parsedDetails: unknown = JSON.parse(taskDetails);
      if (!isTaskDetailsData(parsedDetails)) {
        return;
      }
      const { partId, eventType, eventUser, eventTime } = parsedDetails;

      if (!partId || !eventType || !eventUser || !eventTime) {
        logger.logError("Invalid partId or eventType in taskDetails");
        return;
      }
      let dialogBoxSize: DialogSize;
      dialogBoxSize = {
        width: window.innerWidth / 3.4,
        height: 450,
      };
      const taskInfo = {
        title: "Event Notification",
        url:
          window.location.origin +
          `/index.html#/nxDetailBoard?detailBoardTitle=Notification&partId=${partId}&eventType=${eventType}&eventUser=${eventUser}&eventTime=${eventTime}`,
        card: null,
        size: dialogBoxSize,
      };

      const onDialogClose = () => {
        logger.logInformation("Event Notification dialog closed");
      };
      dialog.url.open(taskInfo, onDialogClose);
    } catch (error) {
      logger.logError(`Failed to parse taskDetails as JSON: ${error}`);
    }
  }, []);

  // handler to redirect the bot conversation if the user is logging in from bot
  const handleRedirection = (): void => {
    let appId = config.appId;
    if (isLoginFromBot) {
      const botUrl = `https://teams.microsoft.com/l/entity/${appId}/conversations`;
      microsoftTeams.app.openLink(botUrl);
      logger.logInformation(
        `Successfully handled redirection with isLoginFromBot: ${JSON.stringify(isLoginFromBot)}`
      );
    }
  };

  // localization handler
  const localizationHandler = useCallback(
    async (locale: string) => {
      const teamcenterLang = LocalizationService.getTeamcenterLanguage(locale);
      const cachedLanguage = teamcenterLanguage ? teamcenterLanguage : "en_US";
      if (cachedLanguage !== teamcenterLang) {
        queryClient.clear();
        setIsAuthenticated(false);
        setIsAuthenticating(false);
        window.localStorage.clear();
      }
      setTeamcenterLanguage(teamcenterLang);
      const i18nMessages =
        await LocalizationService.getMessages(teamcenterLang);
      setI18n(i18nMessages);
    },
    [queryClient, setTeamcenterLanguage, teamcenterLanguage]
  );

  const openDataPrivacyDialog = useCallback(() => {
    const taskInfo = {
      title: i18n.DataPrivacyText,
      url: window.location.origin + "/index.html#/dataprivacyinitial",
      card: null,
      size: { height: 600, width: window.innerWidth > 1000 ? 450 : 350 },
    };
    dialog.url.open(taskInfo, () => queryClient.invalidateQueries());
  }, [i18n.DataPrivacyText, queryClient]);

  const extractAnalyticsInfo = (analyticsResponse: ApiResponse) => {
    let retVal: any = undefined;
    if (analyticsResponse.error) {
      failure.setError(analyticsResponse.error);
    } else if (analyticsResponse.data) {
      retVal = {
        userId: analyticsResponse.data.userId,
        customerId: analyticsResponse.data.customerId,
        tcServerVersion: analyticsResponse.data.tcServerVersion,
        tcPlatformLocale: analyticsResponse.data.tcPlatformLocale,
      };
    } else {
      failure.setError({
        correlationId: "",
        message: "The application could not get analytics info from server.",
        statusCode: 450,
        title: "Failed to get analytics info.",
      });
    }
    return retVal;
  };

  const teamcenter = {
    // using a get functions instead of a classic properties to be sure useApi will re evaluate every time
    get session() {
      return teamcenterCode;
    },

    get isAuthenticated() {
      return isAuthenticated;
    },

    setTCisAuthenticated: (value: boolean) => {
      setIsAuthenticated(value);
    },

    setSession: (value: IAuthCallResponse) => {
      setTeamcenterCode(value);
    },

    get isAuthenticating() {
      return isAuthenticating;
    },

    get isLoginGraph() {
      return isLoginGraph;
    },

    get scopesArray() {
      return scopesArray;
    },

    get siteDisplay() {
      return siteDisplay;
    },

    logout: () => {
      queryClient.clear();
      setIsAuthenticated(false);
      setIsAuthenticating(false);
      //if user is logging out then clear local cache
      window.localStorage.removeItem(Constants.cacheTeamcenterCode);
      window.localStorage.removeItem(Constants.cacheConfiguration);
      window.localStorage.removeItem(Constants.cacheUserConfiguration);
      window.localStorage.removeItem(Constants.cacheTeamcenterLanguage);
      setConfiguration(undefined);
      setUserConfiguration(undefined);
      setTeamcenterCode(undefined);
      setTeamcenterLanguage(undefined);
    },
    login: async () => {
      const selectedSite = Configuration.getValueFromLocalStorage(
        Constants.cacheSelectedSite
      );
      const teamsUserCredential = teamsContext.teamsUserCredential;
      if (!teamsUserCredential) {
        return;
      }

      var sessionId = teamsContext.context?.app?.sessionId;
      if (!sessionId) {
        const context = await app.getContext();
        sessionId = context.app.sessionId;
        if (!sessionId) {
          return;
        }
        setContext(context);
      }
      const configurationResponse: any = await RequestUtils.callTcTeamsApi(
        Constants.opConfiguration,
        teamsUserCredential,
        undefined
      );
      let tcCode: AuthCallResponse = {
        sessionId: undefined,
        userId: undefined,
        userUid: undefined,
        locale: undefined,
        error: undefined,
        siteId: undefined,
        tcVersion: undefined,
        userEmailId: undefined,
        userDisplayName: undefined,
        ssoToken: undefined,
        aadObjectId: undefined,
      };
      if (configurationResponse.error) {
        failure.setError(configurationResponse.error);
      } else if (configurationResponse && configurationResponse.data) {
        setIsAuthenticating(true);
        setConfiguration(configurationResponse.data);
        // Getting the SAM enabled configuration
        const samConfig: ISAMConfig =
          AuthenticationUtils.checkForSAMAuthentication(
            configurationResponse.data
          );
        // Starting the authentication flow
        failure.setError(undefined);
        tcCode = await AuthenticationUtils.authenticateAsync(
          sessionId,
          teamsUserCredential,
          samConfig,
          conversationId,
          tokenSid,
          configurationResponse.data.tcVersion
        );
        const token = await teamsUserCredential.getToken("");
        const ssoToken = token?.token || undefined;
        tcCode.ssoToken = ssoToken;
        tcCode.aadObjectId = teamsContext.context?.user?.id;
        if (tcCode.error) {
          setIsAuthenticated(false);
          failure.setError(tcCode.error);
        } else if (
          tcCode &&
          tcCode.sessionId &&
          tcCode.userId &&
          tcCode.userUid
        ) {
          if (selectedSite && selectedSite.siteId) {
            if (tcCode.siteId && tcCode.siteId === selectedSite["siteId"]) {
              tcCode.siteDisplay = selectedSite["siteDisplay"];
            } else {
              setIsAuthenticated(false);
              logger.logInformation(
                "SiteId from API response doesn't match with the siteId selected."
              );
            }
          }
          if (!selectedSite || !selectedSite.siteId || tcCode.siteDisplay) {
            setIsAuthenticated(true);
            if (!tcCode.ecaId) {
              // Calling the session analytics API to get analytics information.
              const analyticsResponse: ApiResponse =
                await RequestUtils.callTcTeamsApi(
                  Constants.opGetSessionanalyticsInfo,
                  teamsUserCredential,
                  tcCode
                );
              tcCode.analyticsInfo = extractAnalyticsInfo(analyticsResponse);
              tcCode.ecaId = tcCode.analyticsInfo?.customerId;
            }
            setTeamcenterCode(tcCode);
            // Call the API call to get User Configuration
            const userConfigResponse: ApiResponse =
              await RequestUtils.callTcTeamsApi(
                Constants.opUserConfiguration,
                teamsUserCredential,
                tcCode
              );
            if (userConfigResponse.error) {
              failure.setError(userConfigResponse.error);
            } else if (userConfigResponse && userConfigResponse.data) {
              setUserConfiguration(userConfigResponse.data);
              if (configurationResponse.data[Constants.tenantTelemetryOptIn]) {
                const telemetryOptInValue: boolean =
                  configurationResponse.data[Constants.tenantTelemetryOptIn];
                // if (telemetryOptInValue === true) {
                //   const userProductExcellenceTelemetryOptIn: any =
                //     userConfigResponse.data[
                //       Constants.userProductExcellenceTelemetryOptIn
                //     ];
                //   if (userProductExcellenceTelemetryOptIn === null) {
                //     openDataPrivacyDialog();
                //   }
                // }
              }
            }
          }
        } else {
          setIsAuthenticated(false);
        }
      }
      setIsAuthenticating(false);
      // If the user is logging in from bot, open the bot conversation
      handleRedirection();
    },

    setLogin: async (teamcenterCode: IAuthCallResponse) => {
      setTeamcenterCode(teamcenterCode);
      try {
        await teamsContext.teamsUserCredential?.getToken(scopesArray);
        setIsLoginGraph(true);
      } catch (error: any) {
        setIsLoginGraph(false);
        failure.setError(error);
      }
      setIsAuthenticated(true);
    },
  };

  const failure = { setError, error };

  useEffect(() => {
    if (context) {
      if (context.page?.subPageId && !taskDetailOpened) {
        handleIsLoginFromBot(context.page.subPageId);
        handleEventNotification(context.page.subPageId);
        setTaskDetailOpened(true);
      }
    }
  }, [context]);

  // Call localizationHandler on initial context load and locale change
  useEffect(() => {
    if (teamsContext?.context?.app?.locale) {
      localizationHandler(teamsContext.context.app.locale);
    }
  }, [teamsContext?.context?.app?.locale, localizationHandler]);

  useEffect(() => {
    if (teamsContext?.context) {
      if (teamsContext?.context.page?.subPageId && !taskDetailOpened) {
        handleIsLoginFromBot(teamsContext?.context.page.subPageId);
        setTaskDetailOpened(true);
      }
    }
  }, [teamsContext?.context, taskDetailOpened, handleIsLoginFromBot]);

  logger.logTrace("Exit useTcTeamsContext");

  return {
    isLoginFromBot,
    configuration,
    userConfiguration,
    teamcenterLanguage,
    i18n,
    teamcenter,
    failure,
    queryClient,
    scopesArray,
    isLoginGraph,
  };
};
